import React, { useEffect, useMemo, useState } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import { Card, CardBody } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { get } from '../../../hook/request';
import { Modal, ModalBody, ModalFooter, ModalHeader, Button as RSButton } from 'reactstrap';
import { toast } from 'react-toastify';
import Favourite from '../../../Components/Widgets/Favourite'
import { H3 } from '../../../AbstractElements';

// ===== Helpers telefono =====
const onlyDigits = (v: string) => v.replace(/\D+/g, '');
const normalizePhone = (v: string | null) => {
  if (!v) return '';
  const d = onlyDigits(v);
  if (d.startsWith('39') && d.length > 10) return d.slice(2);
  return d;
};
const fmtPhone = (v: string | null) => {
  const d = normalizePhone(v);
  if (!d) return '-';
  return d;
};
const buildWhatsAppHref = (phone: string | null, countryCode = '39') => {
  const d = normalizePhone(phone);
  if (!d) return null;
  // se il numero è già nazionale (10 cifre), anteponi prefisso countryCode
  const num = d.length <= 10 ? `${countryCode}${d}` : d;
  return `https://wa.me/${num}`;
};
const buildTelHref = (phone: string | null) => {
  const d = normalizePhone(phone);
  return d ? `tel:${d}` : null;
};

// ===== Cell telefono con modale =====
const PhoneCell: React.FC<{ phone: string | null }> = ({ phone }) => {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen(v => !v);

  const formattedPhone = fmtPhone(phone);
  const waHref = buildWhatsAppHref(phone, '39');
  const telHref = buildTelHref(phone);

  if (!waHref && !telHref) return <span>{formattedPhone}</span>;

  const onClickCell = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggle();
  };

  const onCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(normalizePhone(phone));
      toast.success('Numero copiato');
    } catch {
      /* noop */
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onClickCell}
        className="btn p-0 text-decoration-none"
        style={{ cursor: 'pointer' }}
      >
        {formattedPhone}
      </button>

      <Modal isOpen={open} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>Contatta</ModalHeader>
        <ModalBody>
          <div className="d-flex flex-column gap-2">
            <div><strong>Numero:</strong> {formattedPhone}</div>
            <div className="text-muted">Scegli l'azione da eseguire:</div>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between flex-wrap gap-2">
          <div className="d-flex gap-2">
            {waHref && (
              <RSButton
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(waHref, '_blank', 'noopener,noreferrer');
                }}
              >
                WhatsApp
              </RSButton>
            )}
            {telHref && (
              <a
                href={telHref}
                onClick={(e) => e.stopPropagation()}
                className="btn btn-primary"
              >
                Chiama
              </a>
            )}
            <RSButton color="secondary" outline onClick={onCopy}>
              Copia numero
            </RSButton>
          </div>
          <RSButton color="light" onClick={(e: any) => { e.stopPropagation(); toggle(); }}>
            Chiudi
          </RSButton>
        </ModalFooter>
      </Modal>
    </>
  );
};

// ===== Tipi lista =====
type RawItem = {
  consumer_cod_id: string;
  cognome: string;
  nome: string;
  telefono: string | null;
  telefono2: string | null;
  citta: string | null;
  data_nascita: string | null;
};

function fmtBirthDate(raw: string | null) {
  if (!raw) return '-';
  const s = String(raw).trim().replace(/["']/g, ''); // rimuove virgolette
  if (!s) return '-';
  const t = s.replace(/[.]/g, '-').replace(/\//g, '-');
  let m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const [, y, mo, d] = m;
    return `${d}/${mo}/${y}`;
  }

  m = t.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (m) {
    const [, d, mo, y] = m;
    return `${d}/${mo}/${y}`;
  }

  return s;
}

export default function AnagraficaList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<RawItem[]>([]);
  const [loading, setLoading] = useState(false);


  const [filterText, setFilterText] = useState('');
  const filteredRows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => {
      const name = `${r.cognome ?? ''} ${r.nome ?? ''}`.toLowerCase();
      const id = (r.consumer_cod_id ?? '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [rows, filterText]);


  const FilterBar = (
    <div className="d-flex align-items-center gap-2 w-100">
      <input
        type="text"
        className="form-control"
        placeholder="Cerca per nome o consumer_id…"
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
      />
      {filterText && (
        <button className="btn btn-light" onClick={() => setFilterText('')}>Pulisci</button>
      )}
    </div>
  );

  const customStyles = {
    rows: {
      style: {
        minHeight: '30px',
      },
    },
    cells: {
      style: {
        paddingTop: '10px',
        paddingBottom: '10px',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    headCells: {
      style: {
        paddingTop: '14px',
        paddingBottom: '14px',
      },
    },
  };


  async function load() {
    setLoading(true);
    try {
      const res = await get<any>('/anagrafica/list');
      const payload = (res as any)?.data ?? res;
      const list: RawItem[] = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];
      setRows(list);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const columns: TableColumn<RawItem>[] = useMemo(
    () => [
      {
        name: 'Nominativo',
        selector: r => `${r.cognome ?? ''} ${r.nome ?? ''}`.trim(),
        sortable: true,
        cell: (r) => (
          <button
            type="button"
            className="btn p-0 text-decoration-none fw-semibold"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/report/anagrafe/${encodeURIComponent(r.consumer_cod_id)}`);
            }}
            title="Apri dettagli"
          >
            {`${r.cognome ?? ''} ${r.nome ?? ''}`.trim() || '-'}
          </button>
        ),
      },
      {
        name: 'Telefono',
        selector: r => normalizePhone(r.telefono ?? r.telefono2 ?? ''),
        sortable: true,
        width: '200px',
        cell: (r) => <PhoneCell phone={r.telefono || r.telefono2} />,
      },
      {
        name: 'Città',
        selector: r => r.citta ?? '',
        sortable: true,
        width: '180px',
        cell: (r) => r.citta || '-',
      },
      {
        name: 'Data di nascita',
        selector: r => r.data_nascita ?? '',
        sortable: true,
        width: '160px',
        right: true,
        cell: (r) => fmtBirthDate(r.data_nascita),
      },
      {
        name: 'Azioni',
        width: '140px',
        button: true,
        ignoreRowClick: true,
        cell: (r) => (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/report/anagrafe/${encodeURIComponent(r.consumer_cod_id)}`);
            }}
            title="Modifica"
          >
            Modifica
          </button>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div className="page-body pt-4">
      <Card>
        <CardBody>
          <div className='d-flex justify-content-between mx-2 my-2'>
            <H3 className="mb-3">Anagrafica</H3>
            <Favourite
              percorso={'/report/anagrafe'}
              nome={'Anagrafica'}
            />
          </div>
          <div className="table-responsive">
            <DataTable
              columns={columns}
              data={filteredRows}
              progressPending={loading}
              striped
              pagination
              highlightOnHover
              noDataComponent="Nessun dato disponibile"
              paginationPerPage={50}
              subHeader
              subHeaderComponent={FilterBar}
              customStyles={customStyles}
            />

          </div>
        </CardBody>
      </Card>
    </div>

  );
}
